import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Plus,
  Pencil,
  BarChart,
  Package,
  Clipboard,
  HandHelping,
  AlertTriangle,
} from "lucide-react";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { AdminDrawerFooter } from "@/components/admin/AdminDrawerFooter";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import {
  adminDrawerBodyStyle,
  adminFieldStyle,
  adminInputStyle,
  adminLabelStyle,
  adminMutedTextStyle,
} from "@/components/admin/adminFormStyles";
import {
  ReportModal,
  type ReportType,
} from "@/components/supplier/ReportModal";
import { MonthlySupplierSummaryModal } from "@/components/supplier/MonthlySupplierSummaryModal";
import { SupplierBoxTransactionModal } from "@/components/supplier/SupplierBoxTransactionModal";
import {
  useSuppliers,
  useCreateSupplier,
  useUpdateSupplier,
  useDeleteSupplier,
  useSupplierReportStatuses,
} from "@/features/supplier/hooks";
import type { Supplier, SupplierReportStatus } from "@/features/supplier/types";
import "./FornecedoresPage.css";

type DrawerMode = "create" | "edit";

interface DrawerState {
  mode: DrawerMode;
  supplier?: Supplier;
}

interface ReportState {
  supplier: Supplier;
  type: ReportType;
}

interface BoxTransactionState {
  supplier: Supplier;
}

const PHONE_DIGIT_LIMIT = 11;
const SUPPLIER_PAGE_SIZE = 10;

const REPORT_BUTTONS: {
  type: ReportType;
  label: string;
  icon: ReactNode;
}[] = [
  {
    type: "mensal",
    label: "Mensal",
    icon: <BarChart data-report-icon="bar-chart" size={14} strokeWidth={1.8} />,
  },
  {
    type: "caixas",
    label: "Caixas",
    icon: <Package data-report-icon="package" size={14} strokeWidth={1.8} />,
  },
  {
    type: "pedido",
    label: "Pedidos",
    icon: (
      <Clipboard data-report-icon="clipboard" size={14} strokeWidth={1.8} />
    ),
  },
];

function getPhoneDigits(value: string) {
  return value.replace(/\D/g, "").slice(0, PHONE_DIGIT_LIMIT);
}

function isValidPhoneDigits(value: string) {
  return value.length === 10 || value.length === 11;
}

function formatPhone(value?: string | null) {
  const digits = getPhoneDigits(value || "");
  if (!digits) return "";
  const area = digits.slice(0, 2);
  if (digits.length <= 2) return `(${area}`;
  if (digits.length <= 6) return `(${area}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${area}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${area}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function formatDateTime(value?: string | null) {
  if (!value) return "Nunca";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatMonth(value?: string | null) {
  if (!value) return "sem referência";
  const [year, month] = value.split("-");
  if (!year || !month) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(new Date(Number(year), Number(month) - 1, 1));
}

function previousMonthKey() {
  const now = new Date();
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${previous.getFullYear()}-${String(previous.getMonth() + 1).padStart(2, "0")}`;
}

function formatBoxBalance(balance: number) {
  const absolute = new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 0,
  }).format(Math.abs(balance));
  if (balance > 0) return `+${absolute} cx`;
  if (balance < 0) return `−${absolute} cx`;
  return "0 cx";
}

function SupplierDirectorySkeleton() {
  return (
    <div
      className="supplier-loading-wrap"
      aria-label="Carregando fornecedores"
      role="status"
    >
      <div className="supplier-loading-row supplier-loading-head">
        <span>Fornecedor</span>
        <span>Caixas</span>
        <span>Relatórios</span>
        <span />
      </div>
      {Array.from({ length: 3 }, (_, index) => (
        <div className="supplier-loading-row" key={index} aria-hidden="true">
          <span className="supplier-skeleton supplier-skeleton-wide" />
          <span className="supplier-skeleton" />
          <span className="supplier-skeleton supplier-skeleton-wide" />
          <span className="supplier-skeleton supplier-skeleton-action" />
        </div>
      ))}
    </div>
  );
}

function mensalCardStatus(status?: SupplierReportStatus) {
  const previousMonth = previousMonthKey();
  if (!status) {
    return {
      label: "Carregando",
      detail: "Buscando histórico mensal",
      tone: "waiting" as const,
    };
  }
  if (status.mensal_status !== "up_to_date") {
    return {
      label: "Pendente",
      detail: `Falta emitir ${formatMonth(status.mensal_due_month ?? previousMonth)}`,
      tone: "pending" as const,
    };
  }
  if (status.mensal_current.last_generated_at) {
    return {
      label: "Emitido",
      detail: `Mês atual emitido em ${formatDateTime(status.mensal_current.last_generated_at)}`,
      tone: "success" as const,
    };
  }
  return {
    label: "Mês em progresso",
    detail: status.last_mensal_export_at
      ? `${formatMonth(previousMonth)} emitido em ${formatDateTime(status.last_mensal_export_at)}`
      : `${formatMonth(previousMonth)} já está cobrado`,
    tone: "progress" as const,
  };
}

function caixasCardStatus(status?: SupplierReportStatus) {
  if (!status?.last_caixas_export_at) {
    return {
      label: "Nunca emitido",
      detail: "Nenhum relatório de caixas registrado",
      tone: "waiting" as const,
    };
  }
  return {
    label: "Emitido",
    detail: `Último relatório em ${formatDateTime(status.last_caixas_export_at)}`,
    tone: "success" as const,
  };
}

function pedidoCardStatus(status?: SupplierReportStatus) {
  if (!status) {
    return {
      label: "Carregando",
      detail: "Buscando fechamento dos pedidos de hoje",
      tone: "waiting" as const,
    };
  }
  if (
    status.pedido_today.needs_export ||
    status.pedido_today.stale_after_generation
  ) {
    return {
      label: "Pendente",
      detail: status.pedido_today.stale_after_generation
        ? "Houve alteração relevante depois da última emissão"
        : "Todos os pedidos de hoje fecharam e ainda falta emitir",
      tone: "pending" as const,
    };
  }
  if (status.pedido_today.ready_to_export && status.last_pedido_export_at) {
    return {
      label: "Emitido",
      detail: `Emitido hoje em ${formatDateTime(status.last_pedido_export_at)}`,
      tone: "success" as const,
    };
  }
  return {
    label: "Aguardando",
    detail: "Aguardando fechamento dos pedidos de hoje",
    tone: "waiting" as const,
  };
}

export function FornecedoresPage() {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { canAudit, canDelete, canManageCatalog } =
    getOperationalCapabilities(currentUser);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const {
    data,
    isLoading,
    isFetching,
    isPlaceholderData,
    error: suppliersError,
    refetch: refetchSuppliers,
  } = useSuppliers({
    limit: SUPPLIER_PAGE_SIZE,
    offset: page * SUPPLIER_PAGE_SIZE,
    search: debouncedSearch,
  });
  const items = data?.items ?? [];
  const visibleSupplierIds = useMemo(
    () => items.map((supplier) => supplier.id),
    [items],
  );
  const {
    data: reportStatusesData,
    error: reportStatusesError,
    refetch: refetchReportStatuses,
  } = useSupplierReportStatuses(canAudit ? visibleSupplierIds : []);
  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const deleteMutation = useDeleteSupplier();

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reportState, setReportState] = useState<ReportState | null>(null);
  const [monthlySummaryOpen, setMonthlySummaryOpen] = useState(false);
  const [boxTransactionState, setBoxTransactionState] =
    useState<BoxTransactionState | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const reportStatuses = useMemo(() => {
    const map = new Map<string, SupplierReportStatus>();
    for (const item of reportStatusesData?.items ?? []) {
      map.set(item.supplier_id, item);
    }
    return map;
  }, [reportStatusesData]);

  function openCreate() {
    if (!canManageCatalog) return;
    setName("");
    setEmail("");
    setPhone("");
    setPhoneError(null);
    setConfirmDelete(false);
    setDrawer({ mode: "create" });
  }

  function openEdit(supplier: Supplier, e: React.MouseEvent) {
    if (!canManageCatalog) return;
    e.stopPropagation();
    setName(supplier.name);
    setEmail(supplier.email || "");
    setPhone(formatPhone(supplier.phone));
    setPhoneError(null);
    setConfirmDelete(false);
    setDrawer({ mode: "edit", supplier });
  }

  function closeDrawer() {
    setDrawer(null);
    setPhoneError(null);
    setConfirmDelete(false);
  }

  function handlePhoneChange(value: string) {
    setPhone(formatPhone(value));
    const digits = getPhoneDigits(value);
    if (!digits || isValidPhoneDigits(digits)) {
      setPhoneError(null);
      return;
    }
    setPhoneError("Use 10 ou 11 dígitos.");
  }

  async function handleSubmit() {
    if (!canManageCatalog || !name.trim()) return;
    const phoneDigits = getPhoneDigits(phone);
    if (phoneDigits && !isValidPhoneDigits(phoneDigits)) {
      setPhoneError("Use 10 ou 11 dígitos.");
      return;
    }

    const payload = {
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phoneDigits ? formatPhone(phone) : undefined,
    };

    if (drawer?.mode === "create") {
      await createMutation.mutateAsync(payload);
    } else if (drawer?.mode === "edit" && drawer.supplier) {
      await updateMutation.mutateAsync({ id: drawer.supplier.id, payload });
    }
    closeDrawer();
  }

  async function handleDelete() {
    if (!canDelete || !drawer?.supplier) return;
    await deleteMutation.mutateAsync(drawer.supplier.id);
    closeDrawer();
  }

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const totalSuppliers = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalSuppliers / SUPPLIER_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const emptyMessage = debouncedSearch
    ? "Nenhum fornecedor encontrado."
    : "Nenhum fornecedor cadastrado.";
  const isUpdating = isFetching && !isLoading;

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  return (
    <>
      <style>{`
        .supplier-add-action {
          justify-content: center;
          text-align: center;
          min-height: 2.75rem;
          border-radius: 4px !important;
        }
        .supplier-header-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem;
        }
        .supplier-summary-action {
          justify-content: center;
          text-align: center;
          min-height: 2.75rem;
        }
        .suppliers-toolbar {
          display: grid;
          gap: 0.45rem;
          align-items: center;
          padding: 0.2rem 0 1.15rem;
          position: relative;
        }
        .suppliers-toolbar > div:first-child {
          width: 100%;
          max-width: none;
        }
        .suppliers-toolbar input {
          border-radius: 4px !important;
          transition: border-color var(--operational-motion-hover) ease, box-shadow var(--operational-motion-hover) ease;
        }
        .suppliers-toolbar input:focus-visible {
          border-color: var(--login-accent) !important;
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--login-accent) 18%, transparent);
        }
        .supplier-fetch-status {
          position: absolute;
          right: 0;
          bottom: 0.15rem;
          color: var(--login-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.72rem;
          line-height: 1;
          opacity: 0;
          pointer-events: none;
          transition: opacity var(--operational-motion-update) ease;
        }
        .supplier-fetch-status[data-visible="true"] {
          opacity: 1;
        }
        .supplier-empty-state {
          min-height: 15rem;
          display: grid;
          place-content: center;
          justify-items: center;
          gap: 0.6rem;
          padding: 1.5rem;
          text-align: center;
          border-top: 1px solid var(--login-border);
          border-bottom: 1px solid var(--login-border);
        }
        .supplier-empty-state strong {
          color: var(--login-text);
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 1.1rem;
        }
        .supplier-empty-state p { margin: 0; }
        .supplier-empty-state button {
          min-height: 2.75rem;
          padding: 0.5rem 0.8rem;
          border: 1px solid var(--login-border);
          border-radius: 4px;
          background: transparent;
          color: var(--login-text);
          cursor: pointer;
          font: 700 0.82rem 'DM Sans', sans-serif;
        }
        .supplier-empty-state button:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }
        .supplier-ledger {
          container-name: supplier-ledger;
          container-type: inline-size;
          border-top: 1px solid var(--login-border);
          border-bottom: 1px solid var(--login-border);
          background: var(--login-surface);
          scrollbar-gutter: stable;
        }
        .supplier-loading-wrap {
          overflow: hidden;
          border-top: 1px solid var(--login-border);
          border-bottom: 1px solid var(--login-border);
          background: var(--login-surface);
          scrollbar-gutter: stable;
        }
        .supplier-loading-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 5.5rem minmax(9rem, 1fr) 2.75rem;
          gap: 1rem;
          min-height: 4.25rem;
          align-items: center;
          padding: 0 var(--operational-row-inset);
          border-bottom: 1px solid var(--login-border);
        }
        .supplier-loading-row:last-child {
          border-bottom: 0;
        }
        .supplier-loading-head {
          min-height: 2.5rem;
          color: var(--login-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
        }
        .supplier-skeleton {
          display: block;
          width: 70%;
          height: 0.72rem;
          border-radius: 4px;
          background: color-mix(in srgb, var(--login-border) 68%, transparent);
          animation: supplierSkeletonPulse 1.4s ease-in-out infinite;
        }
        .supplier-skeleton-wide { width: 90%; }
        .supplier-skeleton-action { width: 2.25rem; height: 2.25rem; }
        @keyframes supplierSkeletonPulse {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 0.82; }
        }
        .supplier-ledger-head {
          position: absolute;
          width: 1px;
          height: 1px;
          margin: -1px;
          padding: 0;
          overflow: hidden;
          clip: rect(0 0 0 0);
          clip-path: inset(50%);
          white-space: nowrap;
          border: 0;
        }
        .supplier-ledger-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 2.75rem;
          grid-template-areas:
            "identity identity"
            "balance balance"
            "divider divider"
            "reports edit";
          gap: 0.7rem 0.75rem;
          align-items: start;
          padding: 1.1rem var(--operational-row-inset) 1rem;
          border-bottom: 1px solid color-mix(in srgb, var(--login-border) 72%, transparent);
          transition: background-color var(--operational-motion-hover) ease;
        }
        .supplier-ledger-row::before {
          content: "";
          grid-area: divider;
          height: 1px;
          background: color-mix(in srgb, var(--login-border) 76%, transparent);
        }
        .supplier-ledger-row:last-child {
          border-bottom: 0;
        }
        .supplier-identity {
          grid-area: identity;
          min-width: 0;
          align-self: center;
        }
        .supplier-name-line {
          display: flex;
          align-items: baseline;
          gap: 0.5rem;
          min-width: 0;
          flex-wrap: wrap;
        }
        .supplier-name {
          margin: 0;
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
          font-size: 1.08rem;
          letter-spacing: 0;
          color: var(--login-text);
          overflow-wrap: anywhere;
        }
        .supplier-meta {
          margin: 0.12rem 0 0;
          color: var(--login-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.84rem;
          overflow-wrap: anywhere;
        }
        .supplier-account-warning {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          color: #b77912;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.69rem;
          font-weight: 700;
          letter-spacing: 0;
          text-transform: uppercase;
        }
        .supplier-balance-action {
          justify-self: start;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          min-height: 2.75rem;
          padding: 0 0.55rem 0 0.5rem;
          border: 0;
          border-left: 3px solid var(--login-border);
          border-radius: 0;
          background: transparent;
          color: var(--login-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0;
          cursor: pointer;
          white-space: nowrap;
          transition: color var(--operational-motion-hover) ease, background-color var(--operational-motion-hover) ease, border-color var(--operational-motion-hover) ease;
        }
        .supplier-balance-cell {
          grid-area: balance;
          min-width: 0;
          align-self: center;
        }
        .supplier-balance-action[data-balance-tone="positive"] {
          border-left-color: var(--login-accent);
          color: color-mix(in srgb, var(--login-accent) 90%, var(--login-text));
        }
        .supplier-balance-action[data-balance-tone="negative"] {
          border-left-color: var(--login-error, #c44e3a);
          color: var(--login-error, #c44e3a);
        }
        .supplier-report-actions {
          grid-area: reports;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.25rem;
          min-width: 0;
          padding-top: 0;
        }
        .supplier-report-action {
          position: relative;
          min-width: 0;
          min-height: 2.75rem;
          padding: 0.38rem 0.3rem;
          border: 0;
          border-radius: 3px;
          background: transparent;
          color: var(--login-text);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.12rem;
          font-family: 'Barlow Condensed', sans-serif;
          cursor: pointer;
          transition: color var(--operational-motion-hover) ease, background-color var(--operational-motion-hover) ease, transform var(--operational-motion-hover) ease;
        }
        .supplier-report-label {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0;
        }
        .supplier-report-state {
          display: block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.65rem;
          font-weight: 500;
          line-height: 1.2;
          color: var(--login-muted);
        }
        .supplier-report-action[data-report-tone="success"] .supplier-report-state,
        .supplier-report-action[data-report-tone="progress"] .supplier-report-state {
          color: var(--login-accent);
        }
        .supplier-report-action[data-report-tone="pending"] .supplier-report-state {
          color: #b77912;
        }
        .supplier-report-detail {
          position: absolute;
          width: 1px;
          height: 1px;
          overflow: hidden;
          clip: rect(0 0 0 0);
          white-space: nowrap;
        }
        .edit-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.75rem;
          height: 2.75rem;
          padding: 0;
          border-radius: 4px;
          background: transparent;
          border: 1px solid var(--login-border);
          color: var(--login-muted);
          cursor: pointer;
          transition: color var(--operational-motion-hover) ease, background-color var(--operational-motion-hover) ease, border-color var(--operational-motion-hover) ease, transform var(--operational-motion-hover) ease;
        }
        .supplier-edit-cell {
          grid-area: edit;
          justify-self: end;
          align-self: stretch;
          display: flex;
          align-items: center;
          padding-top: 0;
        }
        .supplier-balance-action:focus-visible,
        .supplier-report-action:focus-visible,
        .edit-btn:focus-visible,
        .supplier-add-action:focus-visible,
        .supplier-summary-action:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }
        @media (hover: hover) {
          .supplier-ledger-row:hover {
            background: color-mix(in srgb, var(--login-accent) 4%, transparent);
          }
          .supplier-balance-action:hover,
          .supplier-report-action:hover,
          .edit-btn:hover {
            background: color-mix(in srgb, var(--login-accent) 9%, transparent);
            color: var(--login-accent);
          }
          .supplier-report-action:hover,
          .edit-btn:hover,
          .supplier-add-action:hover {
            transform: translateY(-1px);
          }
          .supplier-summary-action:hover {
            border-color: var(--login-accent) !important;
            background: color-mix(in srgb, var(--login-accent) 9%, transparent) !important;
            color: var(--login-accent) !important;
            transform: translateY(-1px);
          }
        }
        @container supplier-ledger (min-width: 620px) and (max-width: 839px) {
          .supplier-ledger-row {
            grid-template-columns: minmax(0, 1fr) minmax(6.5rem, auto) 2.5rem;
            grid-template-areas:
              "identity balance balance"
              "divider divider divider"
              "reports reports edit";
            gap: 0.55rem 1rem;
            align-items: center;
            padding: .75rem var(--operational-row-inset);
          }
          .supplier-edit-cell {
            align-self: stretch;
          }
        }
        @container supplier-ledger (min-width: 840px) {
          .supplier-ledger-head,
          .supplier-ledger-row {
            display: grid;
            grid-template-columns: minmax(16rem, 1.6fr) minmax(6.5rem, 0.55fr) minmax(19rem, 1.25fr) 2.5rem;
            gap: 1rem;
            align-items: center;
          }
          .supplier-ledger-head {
            position: static;
            width: auto;
            height: auto;
            margin: 0;
            min-height: 2.5rem;
            padding: 0 var(--operational-row-inset);
            overflow: visible;
            clip: auto;
            clip-path: none;
            white-space: normal;
            border-bottom: 1px solid var(--login-border);
            background: color-mix(in srgb, var(--login-surface) 88%, var(--login-bg));
            color: var(--login-muted);
            font-family: 'DM Sans', sans-serif;
            font-size: 0.68rem;
            font-weight: 700;
            letter-spacing: 0;
            text-transform: uppercase;
          }
          .supplier-ledger-row {
            grid-template-areas: "identity balance reports edit";
            min-height: 4.25rem;
            padding: .65rem var(--operational-row-inset);
          }
          .supplier-ledger-row::before {
            display: none;
          }
          .supplier-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow-wrap: normal;
          }
          .supplier-meta {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow-wrap: normal;
          }
          .supplier-report-actions {
            display: flex;
            align-items: stretch;
            gap: 0.2rem;
            padding-top: 0;
            border-top: 0;
          }
          .supplier-report-action {
            flex: 1 1 0;
            min-height: 2.45rem;
            padding: 0.3rem 0.4rem;
          }
          .edit-btn {
            width: 2.25rem;
            height: 2.25rem;
          }
          .supplier-edit-cell {
            align-self: center;
            display: block;
            padding-top: 0;
            border-top: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .supplier-ledger-row,
          .supplier-add-action,
          .supplier-summary-action,
          .supplier-balance-action,
          .supplier-report-action,
          .edit-btn,
          .suppliers-toolbar input,
          .supplier-fetch-status {
            animation: none !important;
            transition-duration: 0.01ms !important;
            transform: none !important;
          }
          .supplier-skeleton { animation: none !important; }
        }
        @media (max-width: 619px) {
          .supplier-loading-row {
            grid-template-columns: minmax(0, 1fr) 5rem 2.75rem;
            min-height: 5.5rem;
            gap: 0.6rem;
            padding: 0 var(--operational-row-inset);
          }
          .supplier-loading-row > :nth-child(3) { display: none; }
        }
      `}</style>

      <AdminPageShell
        breadcrumb={
          <AdminBreadcrumb
            items={[
              { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
              { label: "Fornecedores" },
            ]}
          />
        }
        icon={HandHelping}
        title="FORNECEDORES"
        size="wide"
        showIcon={false}
        action={
          <div className="supplier-header-actions">
            {canAudit ? (
              <button
                type="button"
                className="supplier-summary-action"
                onClick={() => setMonthlySummaryOpen(true)}
              >
                Resumo mensal
              </button>
            ) : null}
            {canManageCatalog ? (
              <button
                type="button"
                className="supplier-add-action"
                aria-label="Adicionar fornecedor"
                data-layout="centered-action"
                onClick={openCreate}
              >
                <Plus size={16} strokeWidth={2} />
                <span className="supplier-add-action__full">
                  Adicionar fornecedor
                </span>
                <span className="supplier-add-action__short">Adicionar</span>
              </button>
            ) : null}
          </div>
        }
      >
        <div className="suppliers-toolbar">
          <OperationalSearchField
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nome, telefone ou email"
            ariaLabel="Buscar fornecedores"
          />
          <span
            className="supplier-fetch-status"
            data-visible={isUpdating ? "true" : undefined}
            role={isUpdating ? "status" : undefined}
            aria-live="polite"
          >
            {isUpdating ? "Atualizando fornecedores..." : ""}
          </span>
        </div>

        <OperationalQueryState
          hasData={items.length > 0}
          isLoading={isLoading}
          isFetching={isFetching}
          error={items.length > 0 ? null : suppliersError}
          isFiltered={Boolean(debouncedSearch)}
          onRetry={() => void refetchSuppliers()}
          renderSkeleton={() => <SupplierDirectorySkeleton />}
          renderEmpty={({ filtered }) => (
            <section className="supplier-empty-state">
              <strong>{emptyMessage}</strong>
              <p style={adminMutedTextStyle}>
                {filtered
                  ? "Tente outro nome, telefone ou e-mail."
                  : "Cadastre o primeiro fornecedor para começar."}
              </p>
              <button
                type="button"
                onClick={() => (filtered ? setSearch("") : openCreate())}
              >
                {filtered ? "Limpar busca" : "Adicionar fornecedor"}
              </button>
            </section>
          )}
        >
          <div
            className="operational-directory__updating supplier-ledger"
            role="table"
            aria-label="Fornecedores"
            aria-busy={isUpdating}
            data-placeholder-data={isPlaceholderData ? "true" : undefined}
          >
            <div className="supplier-ledger-head" role="row">
              <span role="columnheader">Fornecedor</span>
              <span role="columnheader">Caixas</span>
              {canAudit ? <span role="columnheader">Relatórios</span> : null}
              {canManageCatalog ? (
                <span role="columnheader" aria-label="Ações" />
              ) : null}
            </div>
            {items.map((supplier) => {
              const status = reportStatuses.get(supplier.id);
              const balance = supplier.box_balance ?? 0;
              const balanceTone =
                balance > 0 ? "positive" : balance < 0 ? "negative" : "neutral";
              const reportStatusesByType = {
                mensal: mensalCardStatus(status),
                caixas: caixasCardStatus(status),
                pedido: pedidoCardStatus(status),
              };

              return (
                <article
                  key={supplier.id}
                  className="supplier-ledger-row"
                  role="row"
                  data-testid={`supplier-row-${supplier.id}`}
                >
                  <div className="supplier-identity" role="cell">
                    <div className="supplier-name-line">
                      <h2 className="supplier-name">{supplier.name}</h2>
                      {!supplier.has_active_user && (
                        <span className="supplier-account-warning">
                          <AlertTriangle size={12} />
                          Sem usuário
                        </span>
                      )}
                    </div>
                    <p className="supplier-meta">
                      {formatPhone(supplier.phone) ||
                        supplier.email ||
                        "Sem contato cadastrado"}
                    </p>
                  </div>

                  <div className="supplier-balance-cell" role="cell">
                    {canAudit ? (
                      <button
                        type="button"
                        className="supplier-balance-action"
                        data-balance-tone={balanceTone}
                        aria-label={`Movimentar caixas de ${supplier.name}: ${formatBoxBalance(balance)}`}
                        title="Movimentar caixas"
                        onClick={() => setBoxTransactionState({ supplier })}
                      >
                        <Package size={13} aria-hidden="true" />
                        {formatBoxBalance(balance)}
                      </button>
                    ) : (
                      <span>{formatBoxBalance(balance)}</span>
                    )}
                  </div>

                  {canAudit ? (
                    <div
                      className="supplier-report-actions"
                      role="cell"
                      data-testid={`supplier-report-actions-${supplier.id}`}
                      data-visual-treatment="flat"
                    >
                      {REPORT_BUTTONS.map((button) => {
                        const reportStatus = reportStatusesByType[button.type];
                        const reportDetailId = `supplier-${supplier.id}-${button.type}-detail`;
                        return (
                          <button
                            key={button.type}
                            type="button"
                            className="supplier-report-action"
                            data-report-tone={reportStatus.tone}
                            aria-label={`${button.label} de ${supplier.name}: ${reportStatus.label}`}
                            aria-describedby={reportDetailId}
                            title={reportStatus.detail}
                            onClick={() =>
                              setReportState({ supplier, type: button.type })
                            }
                          >
                            <span className="supplier-report-label">
                              {button.icon}
                              {button.label}
                            </span>
                            <span className="supplier-report-state">
                              {reportStatus.label}
                            </span>
                            <span
                              id={reportDetailId}
                              className="supplier-report-detail"
                            >
                              {reportStatus.detail}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {canManageCatalog ? (
                    <div className="supplier-edit-cell" role="cell">
                      <button
                        type="button"
                        className="edit-btn"
                        aria-label={`Editar fornecedor ${supplier.name}`}
                        title="Editar fornecedor"
                        onClick={(e) => openEdit(supplier, e)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
          <OperationalPager
            variant="numbered"
            page={safePage}
            pageCount={pageCount}
            totalItems={totalSuppliers}
            limit={SUPPLIER_PAGE_SIZE}
            offset={safePage * SUPPLIER_PAGE_SIZE}
            isFetching={isFetching}
            onPageChange={setPage}
            ariaLabel="Paginação dos fornecedores"
            itemLabel="fornecedor"
            itemLabelPlural="fornecedores"
          />
        </OperationalQueryState>
      </AdminPageShell>

      <AdminDrawer
        open={drawer !== null}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
        visualTreatment="operational-sheet"
        title={
          drawer?.mode === "create" ? "Novo Fornecedor" : "Editar Fornecedor"
        }
      >
        <div style={adminDrawerBodyStyle}>
          <div style={adminFieldStyle}>
            <label htmlFor="supplier-name" style={adminLabelStyle}>
              Nome
            </label>
            <input
              id="supplier-name"
              name="supplier-name"
              style={adminInputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: João das Batatas"
            />
          </div>
          <div style={adminFieldStyle}>
            <label htmlFor="supplier-email" style={adminLabelStyle}>
              E-mail (opcional)
            </label>
            <input
              id="supplier-email"
              name="supplier-email"
              style={adminInputStyle}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ex: joao@email.com"
            />
          </div>
          <div style={adminFieldStyle}>
            <label htmlFor="supplier-phone" style={adminLabelStyle}>
              Telefone (opcional)
            </label>
            <input
              id="supplier-phone"
              name="supplier-phone"
              style={adminInputStyle}
              type="tel"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="Ex: (43) 9 8838-6848"
              aria-invalid={phoneError ? true : undefined}
              aria-describedby={phoneError ? "supplier-phone-error" : undefined}
            />
            {phoneError ? (
              <p
                id="supplier-phone-error"
                role="alert"
                style={{
                  margin: 0,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.75rem",
                  color: "var(--login-error, #e05353)",
                }}
              >
                {phoneError}
              </p>
            ) : null}
          </div>
        </div>

        <AdminDrawerFooter
          mode={drawer?.mode ?? "create"}
          confirmDelete={confirmDelete}
          isPending={isPending}
          submitLabel={drawer?.mode === "create" ? "Criar" : "Salvar"}
          primaryClassName="supplier-drawer-confirm"
          dangerClassName="supplier-drawer-danger"
          onCancel={closeDrawer}
          onSubmit={handleSubmit}
          onRequestDelete={canDelete ? () => setConfirmDelete(true) : undefined}
          onConfirmDelete={canDelete ? handleDelete : undefined}
        />
      </AdminDrawer>

      {canAudit && reportState && (
        <ReportModal
          supplier={reportState.supplier}
          type={reportState.type}
          onClose={() => setReportState(null)}
        />
      )}

      {canAudit ? (
        <MonthlySupplierSummaryModal
          open={monthlySummaryOpen}
          onClose={() => setMonthlySummaryOpen(false)}
        />
      ) : null}

      {canAudit && boxTransactionState && (
        <SupplierBoxTransactionModal
          supplier={boxTransactionState.supplier}
          onClose={() => setBoxTransactionState(null)}
        />
      )}

      {items.length > 0 && suppliersError ? (
        <FloatingToast
          title="Não foi possível atualizar os fornecedores"
          message={getUserFacingErrorMessage(
            suppliersError,
            "Tente novamente em alguns instantes.",
          )}
          actionLabel="Tentar novamente"
          onAction={() => void refetchSuppliers()}
        />
      ) : null}

      {items.length > 0 && reportStatusesError ? (
        <FloatingToast
          title="Não foi possível atualizar os relatórios"
          message={getUserFacingErrorMessage(
            reportStatusesError,
            "Tente novamente em alguns instantes.",
          )}
          actionLabel="Tentar novamente"
          onAction={() => void refetchReportStatuses()}
        />
      ) : null}
    </>
  );
}
